import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Empty, Pagination, Spin, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import {
  getGradeChangeListApi,
  getGrowthValueChangeListApi,
  getMemberCardInfoApi,
  getPointChangeListApi,
} from '@/api/member/member';
import styles from '../../index.module.scss';

interface MemberCardInfo {
  memberCardId?: number | string;
  memberCardName?: string;
  gradeName?: string;
  growthValue?: string;
  totalPoint?: string;
  expiringPoint?: string;
}

interface ChangeRecord {
  eventTime?: string;
  beforeValue?: string;
  afterValue?: string;
  addPoint?: string;
  changeCause?: string;
  remark?: string;
  changeType?: string;
  shopName?: string;
}

export type MemberInfoTabHandle = {
  refresh: () => Promise<void>;
  getCardContext: () => {
    cardList: MemberCardInfo[];
    activeCardId?: string;
    activeCard?: MemberCardInfo;
  };
};

type Props = {
  oneId: string;
  shopId?: number | null;
  memberCardId?: string | null;
  active: boolean;
};

const MemberInfoTab = forwardRef<MemberInfoTabHandle, Props>(({ oneId, shopId, memberCardId, active }, ref) => {
  const [cardList, setCardList] = useState<MemberCardInfo[]>([]);
  const [cardLoading, setCardLoading] = useState(false);
  const [activeCardId, setActiveCardId] = useState<string | undefined>();
  const [changeTabKey, setChangeTabKey] = useState<'grade' | 'point' | 'growth'>('grade');
  const [changeList, setChangeList] = useState<ChangeRecord[]>([]);
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeTotal, setChangeTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    setCardList([]);
    setActiveCardId(undefined);
    setChangeList([]);
    setChangeTotal(0);
    setPageNum(1);
    setPageSize(10);
    setInitialized(false);
  }, [oneId, shopId, memberCardId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!active || !oneId || initialized) return;
    fetchMemberCards();
    setInitialized(true);
  }, [active, initialized, oneId, shopId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!active || !activeCardId) return;
    fetchChangeRecords({ pageNum: 1 });
  }, [active, activeCardId, changeTabKey]);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      const { activeId } = await fetchMemberCards(true);
      if (activeId) {
        fetchChangeRecords({ pageNum: 1 }, activeId);
      }
    },
    getCardContext: () => ({
      cardList,
      activeCardId,
      activeCard: cardList.find(card => String(card.memberCardId) === activeCardId),
    }),
  }));

  const fetchMemberCards = async (forceResetPage?: boolean) => {
    if (!oneId) return { list: [], activeId: undefined as string | undefined };
    setCardLoading(true);
    try {
      const res = await getMemberCardInfoApi({ shopId, oneId });
      if (res?.code === 200 || res?.code === 0) {
        const list: MemberCardInfo[] = res.data || [];
        setCardList(list);
        const routeCardId = memberCardId ? String(memberCardId) : undefined;
        const preferredId =
          (activeCardId &&
            list.some((card: MemberCardInfo) => String(card.memberCardId) === activeCardId) &&
            activeCardId) ||
          routeCardId ||
          (list[0]?.memberCardId !== undefined ? String(list[0].memberCardId) : undefined);
        setActiveCardId(preferredId);
        if (forceResetPage) {
          setPageNum(1);
          setPageSize(10);
        }
        return { list, activeId: preferredId };
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCardLoading(false);
    }
    return { list: [], activeId: undefined };
  };

  const fetchChangeRecords = async (
    override?: Partial<{ pageNum: number; pageSize: number }>,
    targetCardId?: string,
  ) => {
    const cardId = targetCardId ?? activeCardId;
    if (!cardId || !oneId) return;
    const params = {
      memberCardId: cardId,
      oneId,
      shopId,
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    };
    const api =
      changeTabKey === 'grade'
        ? getGradeChangeListApi
        : changeTabKey === 'point'
          ? getPointChangeListApi
          : getGrowthValueChangeListApi;
    setChangeLoading(true);
    try {
      const res = await api(params);
      if (res?.code === 200 || res?.code === 0) {
        setChangeList(res.rows || []);
        setChangeTotal(res.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setChangeLoading(false);
    }
  };

  const changeTabs: TabsProps['items'] = [
    { key: 'grade', label: '等级变更记录' },
    { key: 'point', label: '积分变更记录' },
    { key: 'growth', label: '成长值变更记录' },
  ];

  const renderTableHeader = () => {
    switch (changeTabKey) {
      case 'grade':
        return ['变更时间', '变更前', '变更后', '变更原因', '备注'];
      case 'point':
        return ['变更时间', '变更类型', '变更量', '变更后积分总数', '积分变更原因'];
      case 'growth':
        return ['变更时间', '变更前', '变更后', '变更原因', '备注'];
      default:
        return [];
    }
  };

  const renderTableRow = (record: ChangeRecord, index: number) => {
    if (changeTabKey === 'point') {
      const changeReason = record.changeCause || record.remark || '-';
      const displayReason =
        record.shopName && changeReason !== '-' ? `${changeReason}（${record.shopName}）` : changeReason;
      return (
        <tr key={index}>
          <td>{record.eventTime || '-'}</td>
          <td>{record.changeType || '-'}</td>
          <td>{record.addPoint || '-'}</td>
          <td>{record.afterValue || '-'}</td>
          <td>{displayReason}</td>
        </tr>
      );
    }
    return (
      <tr key={index}>
        <td>{record.eventTime || '-'}</td>
        <td>{record.beforeValue || '-'}</td>
        <td>{record.afterValue || '-'}</td>
        <td>{record.changeCause || '-'}</td>
        <td>{record.remark || '-'}</td>
      </tr>
    );
  };

  return (
    <div className={styles['member-info-section']}>
      <Spin spinning={cardLoading}>
        <div className={styles['member-card-grid']}>
          {cardList.map(card => (
            <div
              key={card.memberCardId}
              className={`${styles['member-card-info']} ${activeCardId === String(card.memberCardId) ? styles.active : ''}`}
              onClick={() => {
                setActiveCardId(card.memberCardId !== undefined ? String(card.memberCardId) : undefined);
                setPageNum(1);
              }}>
              <div className={styles['member-card-info__title']}>{card.memberCardName || '会员卡'}</div>
              <div className={styles['member-card-info__status']}>已入会</div>
              <div className={styles['member-card-info__meta']}>
                <div>
                  <div>当前等级</div>
                  <div>{card.gradeName || '-'}</div>
                </div>
                <div>
                  <div>成长值</div>
                  <div>{card.growthValue || '0'}</div>
                </div>
                <div>
                  <div>积分</div>
                  <div>{card.totalPoint || '0'}</div>
                </div>
              </div>
              <div className={styles['member-card-info__footer']}>即将过期积分：{card.expiringPoint || '0'}</div>
            </div>
          ))}
        </div>
      </Spin>

      <div className={styles['member-info-change']}>
        <Tabs
          items={changeTabs}
          activeKey={changeTabKey}
          onChange={key => {
            setChangeTabKey(key as 'grade' | 'point' | 'growth');
            setPageNum(1);
          }}
        />
        <Spin spinning={changeLoading}>
          <div className={styles['member-info-table']}>
            <table>
              <thead>
                <tr>
                  {renderTableHeader().map(item => (
                    <th key={item}>{item}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {changeList.length ? (
                  changeList.map(renderTableRow)
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="暂无记录"
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Spin>
        <div className={styles['member-info-pagination']}>
          <Pagination
            current={pageNum}
            pageSize={pageSize}
            total={changeTotal}
            showSizeChanger
            showTotal={t => `共 ${t} 条`}
            onChange={(page, size) => fetchChangeRecords({ pageNum: page, pageSize: size })}
          />
        </div>
      </div>
    </div>
  );
});

export default MemberInfoTab;
